<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  headerType: { type: String, default: '' },
  headerText: { type: String, default: '' },
  bodyText: { type: String, default: '' },
  footerText: { type: String, default: '' },
  buttons: { type: Array, default: () => [] },
});

const { t } = useI18n();
</script>

<template>
  <div class="rounded-xl bg-[#ECE5DD] dark:bg-[#0B141A] p-4">
    <!-- Bubble -->
    <div
      class="max-w-[85%] mx-auto rounded-lg rounded-tl-none bg-white dark:bg-[#202C33] shadow-sm overflow-hidden"
    >
      <!-- Header -->
      <div v-if="headerType === 'TEXT'" class="px-3 pt-3 pb-1">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ headerText }}
        </p>
      </div>

      <div
        v-else-if="headerType === 'IMAGE'"
        class="flex items-center justify-center h-32 bg-[#D4E4D0] dark:bg-[#0D4038] overflow-hidden"
      >
        <slot name="header-media">
          <span class="i-lucide-image size-8 text-[#667781]" />
        </slot>
      </div>

      <div
        v-else-if="headerType === 'VIDEO'"
        class="flex items-center justify-center h-32 bg-[#D4E4D0] dark:bg-[#0D4038] overflow-hidden"
      >
        <slot name="header-media">
          <span class="i-lucide-video size-8 text-[#667781]" />
        </slot>
      </div>

      <div
        v-else-if="headerType === 'DOCUMENT'"
        class="flex items-center gap-2 mx-2 mt-2 p-2 rounded-lg bg-[#D4E4D0] dark:bg-[#0D4038]"
      >
        <slot name="header-media">
          <span class="i-lucide-file-text size-5 text-[#667781]" />
          <span class="text-xs text-[#667781]">
            {{ t('WHATSAPP_TEMPLATES.BUBBLE_PREVIEW.DOCUMENT') }}
          </span>
        </slot>
      </div>

      <!-- Body -->
      <div v-if="bodyText" class="px-3 pt-2 pb-1">
        <p class="text-sm whitespace-pre-wrap text-n-slate-12">
          {{ bodyText }}
        </p>
      </div>

      <!-- Footer + Timestamp -->
      <div class="flex items-end justify-between px-3 pb-2">
        <span
          v-if="footerText"
          class="text-[11px] text-[#667781] dark:text-[#8696A0] mr-3"
        >
          {{ footerText }}
        </span>
        <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <span
          class="text-[10px] text-[#667781] dark:text-[#8696A0] ml-auto whitespace-nowrap"
        >
          10:30 AM
          <span
            class="i-lucide-check-check size-3 inline-block align-middle text-[#53BDEB] ml-0.5"
          />
        </span>
        <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      </div>
    </div>

    <!-- Buttons (connected to bubble, Meta style) -->
    <div
      v-if="buttons.length > 0"
      class="max-w-[85%] mx-auto mt-px flex flex-col rounded-b-lg overflow-hidden bg-white dark:bg-[#202C33] shadow-sm"
    >
      <button
        v-for="(button, index) in buttons"
        :key="index"
        type="button"
        class="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium border-t border-[#E9EDEF] dark:border-[#2A3942] text-[#128C7E] cursor-default"
      >
        <span
          v-if="button.type === 'URL'"
          class="i-lucide-external-link size-4"
        />
        <span
          v-else-if="button.type === 'PHONE_NUMBER'"
          class="i-lucide-phone size-4"
        />
        <span
          v-else-if="button.type === 'QUICK_REPLY'"
          class="i-lucide-reply size-4"
        />
        <span
          v-else-if="button.type === 'COPY_CODE'"
          class="i-lucide-copy size-4"
        />
        {{ button.text }}
      </button>
    </div>
  </div>
</template>
